import React, { useState } from "react";
import PropTypes from "prop-types";
import { Avatar, Checkbox, Col, Row } from "antd";
import randomColor from "randomcolor";
import "./styles.css";
const sideSpan = { xxl: 1, xl: 1, lg: 1, md: 2, sm: 3, xs: 3 };
const isHoveredSpan = { xxl: 1, xl: 1, lg: 1, md: 2, sm: 3, xs: 3 };
const mobileActionsSpan = { xxl: 1, xl: 1, lg: 1, md: 2, sm: 3, xs: 6 };

/**
 * @function ListItem
 * @name ListItem
 * @description Generic list item(row) for list component
 * @param {object} props props object
 * @param {object} props.item valid item
 * @param {string} props.name item name
 * @param {string} props.avatarBackgroundColor item avatar background color
 * @param {boolean} props.isSelected select flag
 * @param {Function} props.onSelectItem select callback
 * @param {Function} props.onDeselectItem deselect callback
 * @param {Function} props.renderActions item render actions
 * @param {object[]} props.children item children
 * @returns {object} React Component
 * @version 0.1.0
 * @since 0.1.0
 */
const ListItem = ({
  item,
  name,
  avatarBackgroundColor,
  isSelected,
  onSelectItem,
  onDeselectItem,
  renderActions = () => null,
  onClick,
  children,
  isPackage = true,
}) => {
  // Responsive detection
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  /* eslint-disable no-unused-vars */
  const [isHovered, setHovered] = useState(false);
  const avatarBackground = avatarBackgroundColor || randomColor();

  /**
   * @function
   * @name handleMouseEnter
   * @description Handle on MouseEnter ListItem event
   *
   * @version 0.1.0
   * @since 0.1.0
   */
  const handleMouseEnter = () => {
    setHovered(true);
  };

  /**
   * @function
   * @name handleMouseEnter
   * @description Handle on MouseLeave ListItem event
   *
   * @version 0.1.0
   * @since 0.1.0
   */
  const handleMouseLeave = () => {
    setHovered(false);
  };

  /**
   * @function
   * @name handleToggleSelect
   * @description Handle Toggling List Item checkbox
   *
   * @param {object} event - Event object
   *
   * @version 0.1.0
   * @since 0.1.0
   */
  const handleClickItem = () => {
    if (onClick) {
      onClick();
    }
  };
  const handleToggleSelect = (event) => {
    if (event.target.checked) {
      onSelectItem();
    } else {
      onDeselectItem();
    }
  };

  const renderSideComponent = () => {
    if (isMobile) return null;
    if (isSelected) {
      return (
        <Checkbox
          className="Checkbox"
          onChange={handleToggleSelect}
          checked={isSelected}
        />
      );
    }
    // Safely compute an initial to display without assuming nested props exist
    const displayName =
      name ||
      (item && item.hr_type && item.hr_type.name) ||
      (item && (item.name || item.title || item.first_name)) ||
      "-";
    const initial = String(displayName || "-").toUpperCase().charAt(0);
    return (
      isPackage && (
        <Avatar style={{ backgroundColor: avatarBackground }}>{initial}</Avatar>
      )
    );
  };

  const actions = renderActions();

  return (
    <div
      className="ListItem"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClickItem}
    >
      <Row align="middle">
        {/* eslint-disable react/jsx-props-no-spreading */}
        {!isMobile && <Col {...sideSpan}>{renderSideComponent()}</Col>}
        {children}
        {/* <Link to="/SingleInitiative" >{children}</Link> */}
        {actions && (
          <Col {...(isMobile ? mobileActionsSpan : isHoveredSpan)} style={isMobile ? { textAlign: 'right' } : {}}>
            {/* eslint-enable react/jsx-props-no-spreading */}
            {actions}
          </Col>
        )}
      </Row>
      <div></div>
    </div>
  );
};

ListItem.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  name: PropTypes.string,
  avatarBackgroundColor: PropTypes.string,
  isSelected: PropTypes.bool,
  onSelectItem: PropTypes.func,
  onDeselectItem: PropTypes.func,
  renderActions: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

ListItem.defaultProps = {
  name: undefined,
  avatarBackgroundColor: undefined,
  renderActions: () => null,
  onClick: () => {},
};

export default ListItem;
